import type { FunctionRef } from './types'
import { generateId } from './utils'

export type FunctionRegistry = Map<string, (...args: unknown[]) => unknown>

export function createFunctionRegistry(): FunctionRegistry {
  return new Map()
}

/**
 * Serialize props for postMessage transport.
 * Functions are replaced with `{ __bridge_fn__: true, id, name }` references
 * and registered in the registry for later invocation.
 */
export function serializeProps(props: Record<string, unknown>, registry: FunctionRegistry): Record<string, unknown> {
  const serialized: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(props)) {
    if (typeof value === 'function') {
      const id = `fn_${generateId()}`
      registry.set(id, value as (...args: unknown[]) => unknown)
      serialized[key] = { __bridge_fn__: true, id, name: key } satisfies FunctionRef
    } else if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      serialized[key] = serializeProps(value as Record<string, unknown>, registry)
    } else {
      serialized[key] = value
    }
  }

  return serialized
}

export function isFunctionRef(value: unknown): value is FunctionRef {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as Record<string, unknown>).__bridge_fn__ === true &&
    typeof (value as Record<string, unknown>).id === 'string'
  )
}

/**
 * Deserialize props received via postMessage.
 * Function references are replaced with proxies that invoke the
 * remote function via the provided `invoke` callback.
 */
export function deserializeProps(serialized: Record<string, unknown>, invoke: (funcId: string, args: unknown[]) => Promise<unknown>): Record<string, unknown> {
  const deserialized: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(serialized)) {
    if (isFunctionRef(value)) {
      const funcId = value.id
      deserialized[key] = (...args: unknown[]) => invoke(funcId, args)
    } else if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      deserialized[key] = deserializeProps(value as Record<string, unknown>, invoke)
    } else {
      deserialized[key] = value
    }
  }

  return deserialized
}
